<script lang="ts">
import { defineComponent } from 'vue';
import { FilterBetSlipStatus, useBetsStore } from '@/stores/bets';
import { mapActions, mapState, mapWritableState } from 'pinia';
import Headline from '@/components/headline.vue';
import Bets from '@/components/bets.vue';
import Pagination from '@/components/pagination.vue';
import { useCurrencyStore } from '@/stores/currencies';

function isStatus(value: string): value is FilterBetSlipStatus {
  return Object.values(FilterBetSlipStatus).includes(value as FilterBetSlipStatus);
}

export default defineComponent({
  name: 'bets-page',
  components: { Pagination, Headline, Bets },
  computed: {
    ...mapState(useCurrencyStore, ['currencyList']),
    ...mapState(useBetsStore, ['records', 'totals', 'count', 'loading']),
    ...mapWritableState(useBetsStore, [
      'fromDate',
      'toDate',
      'size',
      'offset',
      'skipResolved',
      'status',
      'userIds',
      'currencies',
    ]),
    getCurrencies() {
      return this.currencyList.map((c) => ({ name: c }));
    },
    inputUsers: {
      get() {
        return this.userIds ? this.userIds.join(',') : null;
      },
      set(v: string | null) {
        if (v) {
          this.userIds = (v as string)
            .replace(/[^\d,]+/, '')
            .split(',')
            .filter((c) => c)
            .map((c) => parseInt(c, 10));
        } else {
          this.userIds = null;
        }
      },
    },
    inputCurrencies: {
      get() {
        return this.currencies ? this.currencies.join(',') : null;
      },
      set(v: string | null) {
        if (v) {
          this.currencies = (v as string)
            .split(',')
            .filter((c) => c)
            .map((c) => c.toUpperCase());
        } else {
          this.currencies = null;
        }
      },
    },
    toggleSkipResolved: {
      get() {
        return this.skipResolved === 'true';
      },
      set(v: boolean) {
        this.skipResolved = v ? 'true' : 'false';
      },
    },
  },
  methods: {
    ...mapActions(useBetsStore, ['fetchRecords']),
    fetchFilteredBets() {
      this.fetchRecords({
        ...(this.inputCurrencies ? { currencies: this.currencies } : {}),
        ...(this.inputUsers ? { userIds: this.userIds } : {}),
        open: this.skipResolved,
      });
    },
    flipBetsPage(newOffset: number) {
      this.offset = newOffset;
      this.fetchFilteredBets();
    },
    resizeBetsPage(newSize: number) {
      this.size = newSize;
      this.fetchFilteredBets();
    },
    apply() {
      this.offset = 0;
      this.fetchFilteredBets();
    },
    reset() {
      this.offset = 0;
      this.fromDate = null;
      this.toDate = null;
      this.inputCurrencies = null;
      this.inputUsers = null;
      this.fetchFilteredBets();
    },
    filterUser(userId: number) {
      this.inputUsers = userId.toString();
      this.apply();
    },
    filterCurrency(currency: string) {
      this.inputCurrencies = currency;
      this.apply();
    },
    onToDateSelected(value: Date | Date[] | (Date | null)[] | null | undefined) {
      if (value) {
        const endOfDay = new Date(value as Date);
        endOfDay.setHours(23, 59, 59, 999);
        this.toDate = endOfDay;
      } else {
        this.toDate = null;
      }
    },
  },
  mounted() {
    this.$router.isReady().then(() => {
      let fetch = false;
      if (this.$route?.query?.status && isStatus(this.$route?.query?.status as string)) {
        this.status = this.$route?.query.status as FilterBetSlipStatus;
      }
      if (this.$route?.query?.userId) {
        this.inputUsers = this.$route.query.userId?.toString() as string;
        fetch = true;
      }
      if (this.$route?.query?.currency) {
        this.inputCurrencies = this.$route.query.currency?.toString() as string;
        fetch = true;
      }

      if (!this.records || fetch) {
        this.fetchFilteredBets();
      }
    });
  },
  watch: {
    skipResolved() {
      this.fetchFilteredBets();
    },
    userIds() {
      this.fetchFilteredBets();
    },
    currencies() {
      this.fetchFilteredBets();
    },
    fromDate() {
      this.fetchFilteredBets();
    },
    toDate() {
      this.fetchFilteredBets();
    }
  }
});
</script>

<template>
  <template v-if="!records && loading">
    <div class="absolute left-80 right-0 top-0 bottom-0 grid items-center text-center">
      <div>
        <i class="pi pi-spinner animate-spin text-gray-200" style="font-size: 5em" />
      </div>
    </div>
  </template>
  <template v-else-if="!records">
    <div class="custom-background" style="height: 90vh">Нет записей или доступ ограничен</div>
  </template>
  <template v-else>
    <teleport to="#title">
      <headline class="mx-4 my-2">Ставки (детально)</headline>
    </teleport>
    <div
      class="inline-grid filters text-xs mb-4 mx-2 max-w-full"
      style="grid-template-columns: 1fr minmax(30vw, max-content) min-content"
    >
      <Fieldset legend="Даты (включительно, UTC+0)" class="border-slate-600">
        <DatePicker
          v-model="fromDate"
          showIcon
          fluid
          iconDisplay="input"
          placeholder="Начало"
          class="my-2"
          dateFormat="dd.mm.yy"
        />
        <DatePicker
          v-model="toDate"
          showIcon
          fluid
          iconDisplay="input"
          placeholder="Конец"
          class="my-2"
          dateFormat="dd.mm.yy"
          @update:modelValue="onToDateSelected"
        />
      </Fieldset>

      <Fieldset legend="Фильтры">
        <MultiSelect
          v-model="currencies"
          display="chip"
          :options="getCurrencies"
          optionLabel="name"
          optionValue="name"
          filter
          fluid
          placeholder="Валюты"
          class="mt-2 mb-1"
          style="max-width: 30vw"
        />
        <InputText
          type="text"
          v-model="inputUsers"
          placeholder="ID пользователей через запятую"
          fluid
          class="my-1"
        />
        <div class="flex my-2 ml-1">
          <ToggleSwitch v-model="toggleSkipResolved" size="sm" />
          <label class="ml-2 text-sm mt-0.5">Только незакрытые</label>
        </div>
      </Fieldset>
      <div class="h-100 flex-col flex justify-end mx-1">
        <Button fluid class="btn btn-trans-dark mt-1" @click="reset">Сбросить</Button>
      </div>
    </div>
    <bets
      :records="records"
      :totals="totals"
      :filterUser="filterUser"
      :filterCurrency="filterCurrency"
      :pageSize="size"
    />
    <Pagination
      :offset="offset"
      :size="size"
      :count="count"
      @flip="flipBetsPage"
      @resize="resizeBetsPage"
      :resizable="true"
    />
  </template>
</template>
